import { useState } from "react";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Menu, Scale, LucideIcon } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { usePortalNav } from "@/hooks/usePortalNav";

const JURIDICO_MENU_ID = 26;

const iconMap: Record<string, LucideIcon> = {
  "/admin/portal-juridico/inicio": Home,
  "/admin/portal-juridico/casos-cobranza": Scale,
};

export const PortalJuridicoLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const NAV = usePortalNav(JURIDICO_MENU_ID, iconMap, Home);

  const isSuperAdmin = profile?.rol_id === 1 || profile?.rol_id === 2;
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");
  const go = (p: string) => { navigate(p); setMobileOpen(false); };

  const rawName = profile?.nombre || profile?.email?.split("@")[0] || "Usuario";
  const userName = rawName.trim().split(/\s+/).slice(0, 2).join(" ");
  const userRole = profile?.rol_nombre ?? "Jurídico";
  const initials = userName.split(" ").filter(Boolean).slice(0, 2).map((p: string) => p.charAt(0).toUpperCase()).join("") || "U";
  const fotoVista = useFotoPerfilVista();

  const currentSection = NAV.find((i) => isActive(i.path))?.label || "Jurídico";

  const sidebar = (
    <PortalSidebar
      portal="Portal Jurídico"
      groups={[{
        items: NAV.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => go(item.path),
        })),
      }]}
      usuario={{ nombre: userName, rol: userRole, iniciales: initials }}
      onRegresar={canReturnToAdmin ? () => go("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
    <PortalTrackingProvider portal="juridico">
      <div className="min-h-screen flex antialiased">
        {/* Desktop sidebar */}
        <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
          {sidebar}
        </aside>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
            {sidebar}
          </SheetContent>
        </Sheet>

        <div className="flex-1 lg:pl-64 min-w-0">
          {/* Mobile header */}
          <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card border-b border-border">
            <div className="flex items-center px-4 pt-3 pb-2 gap-3">
              <button
                onClick={() => setMobileOpen(true)}
                className="p-1.5 -ml-1 rounded-md text-foreground hover:bg-muted transition-colors"
                aria-label="Abrir menú"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-foreground tracking-tight truncate">{currentSection}</p>
              </div>
              <AvatarPersona nombre={userName} fotoUrl={fotoVista} size="sm" />
            </div>
          </header>

          <main className="px-8 py-4 bg-background min-h-screen">
            <Outlet />
          </main>
        </div>
      </div>
    </PortalTrackingProvider>
  );
};
